import {
  addMemberJoin,
  authenticateMemberLogin,
  authenticateMemberLoginInfo,
  authenticateMemberLogout,
  fetchASPRefreshUserInfo,
  fetchAuthCheck,
  fetchBjInfo,
  fetchChangePassword,
  fetchDeleteProfile,
  fetchEditChannel,
  fetchEditFanGrade,
  fetchMemberAuth,
  fetchMemberCheckId,
  fetchMemberCheckIdExits,
  fetchMemberCheckNick,
  fetchMemberEditInfo,
  fetchMemberFind,
  fetchMemberFindChangePassword,
  fetchMemberIdInfo,
  fetchMemberLeave,
  fetchMemberNickChangStatusConfirm,
  fetchMemberSocialJoin,
  fetchProtectRecovery,
  fetchRecoveryId,
  fetchUploadBanner,
  fetchUploadProfile,
} from '@module/apis/client-side/member';
import type {
  AddMemberJoinParams,
  AddMemberJoinResponse,
  ASPRefreshUserInfoResponse,
  AuthCheckParams,
  BjInfoParams,
  BjInfoResponse,
  ChangePasswordParams,
  DeleteProfileParams,
  EditChannelParams,
  EditFanGradeParams,
  FetchMemberFindResponse,
  MemberAccountInfoParams,
  MemberAccountInfoResponse,
  MemberAuthParams,
  MemberCheckIdExitsParams,
  MemberCheckIdExitsResponse,
  MemberCheckIdParams,
  MemberCheckIdResponse,
  MemberCheckNickParams,
  MemberCheckNickResponse,
  MemberEditInfoParams,
  MemberFindChangePasswordParams,
  MemberFindParams,
  MemberLoginInfoParams,
  MemberLoginParams,
  MemberLoginResponse,
  MemberLogoutResponse,
  MemberMemberLeaveParams,
  MemberMemberLeaveResponse,
  MemberNickChangeStatusRespanse,
  MemberSocialJoinParams,
  MemberSocialJoinResponse,
  ProtectRecoveryParams,
  RecoveryIdParams,
  RecoveryIdResponse,
  UploadBannerParams,
  UploadProfileParams,
} from '@module/apis/client-side/member/type';
import type { ApiBaseResponse } from '@module/apis/type';
import {
  keepPreviousData,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { memberQueryKeys } from './memberQueryKeys';

export const useMemberLoginMutation = (options?: UseMutationOptions<MemberLoginResponse, Error, MemberLoginParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.authenticateMemberLogin().queryKey,
    mutationFn: (memberLoginParmas: MemberLoginParams) => authenticateMemberLogin(memberLoginParmas),
    ...options,
  });

export const useMemberLogoutMutation = (options?: UseMutationOptions<MemberLogoutResponse, Error>) =>
  useMutation({
    mutationKey: memberQueryKeys.authenticateMemberLogout().queryKey,
    mutationFn: () => authenticateMemberLogout(),
    ...options,
  });

export const useMemberCheckIdMutation = (
  memberCheckIdParams: MemberCheckIdParams,
  options?: UseMutationOptions<MemberCheckIdResponse, Error>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberCheckId(memberCheckIdParams).queryKey,
    mutationFn: () => fetchMemberCheckId(memberCheckIdParams),
    ...options,
  });

export const useMemberCheckNickMutation = (
  memberCheckNickParams: MemberCheckNickParams,
  options?: UseMutationOptions<MemberCheckNickResponse, Error>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberCheckNick(memberCheckNickParams).queryKey,
    mutationFn: () => fetchMemberCheckNick(memberCheckNickParams),
    ...options,
  });

export const useMemberJoinMutation = (
  options?: UseMutationOptions<AddMemberJoinResponse, Error, AddMemberJoinParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.addMemberJoin().queryKey,
    mutationFn: (fnArgs: AddMemberJoinParams) => addMemberJoin(fnArgs),
    ...options,
  });

export const useMemberSocialJoinMutation = (
  options?: UseMutationOptions<MemberSocialJoinResponse, Error, MemberSocialJoinParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberSocialJoin().queryKey,
    mutationFn: (params: MemberSocialJoinParams) => fetchMemberSocialJoin(params),
    ...options,
  });

export const useMemberLoginInfoMutation = (
  memberLoginInfoParams: MemberLoginInfoParams,
  options?: UseMutationOptions<MemberLoginResponse, Error>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.authenticateMemberLoginInfo().queryKey,
    mutationFn: () => authenticateMemberLoginInfo(memberLoginInfoParams),
    ...options,
  });

export const useMemberLoginInfoQuery = (
  memberLoginInfoParams: MemberLoginInfoParams,
  options?: CustomUseQueryOptions<MemberLoginResponse, Error>,
) => {
  const query = useQuery({
    queryKey: memberQueryKeys.fetchMemberLoginInfo().queryKey,
    queryFn: () => authenticateMemberLoginInfo(memberLoginInfoParams),
    ...options,
  });

  return query;
};

export const useASPRefreshUserInfoQuery = (options?: CustomUseQueryOptions<ASPRefreshUserInfoResponse, Error>) => {
  const query = useQuery({
    queryKey: memberQueryKeys.fetchASPRefreshUserInfo().queryKey,
    queryFn: () => fetchASPRefreshUserInfo(),
    ...options,
  });

  return query;
};

export const useMemberCheckIdExitsMutation = (
  memberCheckIdExitsParams: MemberCheckIdExitsParams,
  options?: UseMutationOptions<MemberCheckIdExitsResponse, Error>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberCheckIdExits(memberCheckIdExitsParams).queryKey,
    mutationFn: () => fetchMemberCheckIdExits(memberCheckIdExitsParams),
    ...options,
  });

export const useMemberFindChangePasswordMutation = (
  options?: UseMutationOptions<MemberCheckIdExitsResponse, Error, MemberFindChangePasswordParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberFindChangePassword().queryKey,
    mutationFn: (params: MemberFindChangePasswordParams) => fetchMemberFindChangePassword(params),
    ...options,
  });

export const useChangePasswordMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, ChangePasswordParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchChangePassword().queryKey,
    mutationFn: (params: ChangePasswordParams) => fetchChangePassword(params),
    ...options,
  });

export const useMemberAccountInfoQuery = (
  memberInfoByIdParams: MemberAccountInfoParams,
  options?: CustomUseQueryOptions<MemberAccountInfoResponse, Error>,
) =>
  useQuery({
    queryKey: memberQueryKeys.fetchMemberIdInfo(memberInfoByIdParams).queryKey,
    queryFn: () => fetchMemberIdInfo(memberInfoByIdParams),
    ...options,
  });

export const useMemberEditInfoMutation = (
  options?: UseMutationOptions<MemberCheckNickResponse, Error, MemberEditInfoParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberEditInfo().queryKey,
    mutationFn: (EditInfoParams: MemberEditInfoParams) => fetchMemberEditInfo(EditInfoParams),
    ...options,
  });

export const useMemberNickChangeStatusMutation = (
  options?: UseMutationOptions<MemberNickChangeStatusRespanse, Error>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchNickChangeStatus().queryKey,
    mutationFn: () => fetchMemberNickChangStatusConfirm(),
    ...options,
  });

export const useDeleteProfileMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, DeleteProfileParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchDeleteProfile().queryKey,
    mutationFn: (deleteProfileParams: DeleteProfileParams) => fetchDeleteProfile(deleteProfileParams),
    ...options,
  });

export const useUploadProfileMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, UploadProfileParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchUploadProfile().queryKey,
    mutationFn: (uploadProfileParams: UploadProfileParams) => fetchUploadProfile(uploadProfileParams),
    ...options,
  });

export const useBjInfoQuery = (bjInfoParams: BjInfoParams, options?: CustomUseQueryOptions<BjInfoResponse, Error>) =>
  useQuery({
    queryKey: memberQueryKeys.fetchBjInfo(bjInfoParams).queryKey,
    queryFn: () => fetchBjInfo(bjInfoParams),
    staleTime: TimeConstants.TEN_MINUTES,
    placeholderData: keepPreviousData,
    ...options,
  });

export const useBjInfoMutation = (bjInfoParams: BjInfoParams, options?: UseMutationOptions<BjInfoResponse, Error>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchBjInfo(bjInfoParams).queryKey,
    mutationFn: () => fetchBjInfo(bjInfoParams),
    ...options,
  });

export const useEditChannelMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, EditChannelParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchEditChannel().queryKey,
    mutationFn: (editChannelParams: EditChannelParams) => fetchEditChannel(editChannelParams),
    ...options,
  });

export const useEditFanGradeMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, EditFanGradeParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchEditFanGrade().queryKey,
    mutationFn: (editFanGradeParams: EditFanGradeParams) => fetchEditFanGrade(editFanGradeParams),
    ...options,
  });

export const useFetchMemberLeaveMutation = (
  options?: UseMutationOptions<MemberMemberLeaveResponse, Error, MemberMemberLeaveParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberLeave().queryKey,
    mutationFn: (memberMemberLeaveParams: MemberMemberLeaveParams) => fetchMemberLeave(memberMemberLeaveParams),
    ...options,
  });

export const useFetchAuthCheckMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, AuthCheckParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchAuthCheck().queryKey,
    mutationFn: (fetchAuthCheckParams: AuthCheckParams) => fetchAuthCheck(fetchAuthCheckParams),
    ...options,
  });

export const useMemberAuthMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, MemberAuthParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchMemberAuth().queryKey,
    mutationFn: (params: MemberAuthParams) => fetchMemberAuth(params),
    ...options,
  });

export const useMemberFindQuery = (
  params: MemberFindParams,
  options?: CustomUseQueryOptions<FetchMemberFindResponse, Error>,
) =>
  useQuery({
    queryKey: memberQueryKeys.fetchMemberFind(params).queryKey,
    queryFn: () => fetchMemberFind(params),
    ...options,
  });

export const useFetchMemberFindInfiniteQuery = (
  params: MemberFindParams,
  options?: UseInfiniteQueryOptions<FetchMemberFindResponse, Error>,
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: memberQueryKeys.fetchMemberFind(params).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = (params.limit ?? 4) * ((actualPageParam || 1) - 1);
      return fetchMemberFind({ ...params, offset });
    },
    ...options,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data?.pages.flatMap((page) => page.list ?? []),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

export const useRecoveryIdQuery = (
  params: RecoveryIdParams,
  options?: CustomUseQueryOptions<RecoveryIdResponse, Error>,
) =>
  useQuery({
    queryKey: memberQueryKeys.fetchRecoveryId(params).queryKey,
    queryFn: () => fetchRecoveryId(params),
    ...options,
  });

export const useProtectRecoveryMutation = (
  options?: UseMutationOptions<ApiBaseResponse, Error, ProtectRecoveryParams>,
) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchProtectRecovery().queryKey,
    mutationFn: (params: ProtectRecoveryParams) => fetchProtectRecovery(params),
    ...options,
  });

export const useUploadBannerMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, UploadBannerParams>) =>
  useMutation({
    mutationKey: memberQueryKeys.fetchUploadBanner().queryKey,
    mutationFn: (params: UploadBannerParams) => fetchUploadBanner(params),
    ...options,
  });
